// PotentialEnergy.js
import React, { useState, useCallback } from "react";
import MainCalculatorsList from "./MainCalculators";
import SharedHelmet from "../Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
const TITLE = "Menghitung Energi Potensial - Hitung Energi Potensial, Massa, atau Ketinggian";
const DESCRIPTION =
  "Kalkulator energi potensial komprehensif: Hitung energi potensial, massa, atau ketinggian berdasarkan nilai input massa, ketinggian, atau energi potensial. Alat yang akurat dan mudah digunakan.";
const KEYWORDS =
  "energi potensial, menghitung energi potensial, kalkulator energi potensial, menghitung massa, menghitung ketinggian, fisika";
const OG_URL = "https://do-calculate.com/calculator/id/potentialenergy";
function PotentialEnergyCalculator() {
  const [mass, setMass] = useState("");
  const [height, setHeight] = useState("");
  const [potentialEnergy, setPotentialEnergy] = useState("");
  const [calculationMode, setCalculationMode] = useState(
    "calculatePotentialEnergy"
  );
  const [error, setError] = useState("");
  const [showSteps, setShowSteps] = useState(false);
  const updateCalculations = useCallback(() => {
    setError("");
    const massValue = parseInt(mass, 10) || 0;
    const heightValue = parseInt(height, 10) || 0;
    const potentialEnergyValue = parseInt(potentialEnergy, 10) || 0;
    switch (calculationMode) {
      case "calculatePotentialEnergy":
        if (massValue > 0 && heightValue > 0) {
          const calculatedPotentialEnergy = 9.81 * massValue * heightValue;
          setPotentialEnergy(Math.round(calculatedPotentialEnergy));
        } else {
          setError("Harap masukkan massa dan ketinggian dengan benar");
        }
        break;
      case "calculateMass":
        if (potentialEnergyValue > 0 && heightValue > 0) {
          const calculatedMass = potentialEnergyValue / (9.81 * heightValue);
          setMass(Math.round(calculatedMass));
        } else {
          setError("Harap masukkan energi potensial dan ketinggian dengan benar");
        }
        break;
      case "calculateHeight":
        if (potentialEnergyValue > 0 && massValue > 0) {
          const calculatedHeight = potentialEnergyValue / (9.81 * massValue);
          setHeight(Math.round(calculatedHeight));
        } else {
          setError("Harap masukkan energi potensial dan massa dengan benar");
        }
        break;
      default:
        break;
    }
  }, [mass, height, potentialEnergy, calculationMode]);
  const handleCalculateClick = () => {
    updateCalculations();
    setShowSteps(true);
  };
  const handleMassChange = (e) => setMass(e.target.value);
  const handleHeightChange = (e) => setHeight(e.target.value);
  const handlePotentialEnergyChange = (e) => setPotentialEnergy(e.target.value);
  const handleModeChange = (e) => setCalculationMode(e.target.value);
  const getSolutionSteps = () => {
    switch (calculationMode) {
      case "calculatePotentialEnergy":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Energi Potensial:</h4>
            <ol>
              <li>Masukkan massa (dalam kilogram) pada kolom yang disediakan.</li>
              <li>Masukkan ketinggian (dalam meter) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan energi potensial.</li>
              <li>Energi potensial sama dengan 9.81 × massa × ketinggian.</li>
            </ol>
          </div>
        );
      case "calculateMass":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Massa:</h4>
            <ol>
              <li>Masukkan energi potensial (dalam joule) pada kolom yang disediakan.</li>
              <li>Masukkan ketinggian (dalam meter) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan massa.</li>
              <li>Massa sama dengan energi potensial / (9.81 × ketinggian).</li>
            </ol>
          </div>
        );
      case "calculateHeight":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Ketinggian:</h4>
            <ol>
              <li>Masukkan energi potensial (dalam joule) pada kolom yang disediakan.</li>
              <li>Masukkan massa (dalam kilogram) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan ketinggian.</li>
              <li>Ketinggian sama dengan energi potensial / (9.81 × massa).</li>
            </ol>
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <div>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "50px" }}
          >
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <p>
                  Kalkulator energi potensial ini memungkinkan Anda melakukan tiga jenis perhitungan
                  berdasarkan nilai input. Pilih jenis perhitungan yang sesuai dan masukkan
                  nilai yang tersedia untuk menghitung nilai yang hilang dengan akurat dan mudah.
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Energi Potensial dengan Massa dan Ketinggian yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui massa (dalam kilogram) dan ketinggian (dalam meter), Anda dapat
                  menghitung energi potensial (dalam joule) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Energi Potensial = 9.81 × Massa × Ketinggian</b>
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Massa dengan Energi Potensial dan Ketinggian yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui energi potensial (dalam joule) dan ketinggian (dalam meter),
                  Anda dapat menghitung massa (dalam kilogram) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Massa = Energi Potensial / (9.81 × Ketinggian)</b>
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Ketinggian dengan Massa dan Energi Potensial yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui massa (dalam kilogram) dan energi potensial (dalam joule),
                  Anda dapat menghitung ketinggian (dalam meter) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Ketinggian = Energi Potensial / (9.81 × Massa)</b>
                </p>
                <p>
                  Gunakan alat ini untuk memasukkan nilai yang tersedia dan menghitung nilai yang hilang
                  dengan akurat, memudahkan Anda melakukan perhitungan fisika terkait energi potensial.
                </p>
                <hr />
              </div>
            </div>
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE}</h2>
                </div>
                <hr />
                <div >
                  <div className="form-group align-items-center">
                    <b>
                      <label
                        className="form-label ms-2"
                        htmlFor="calculationMode"
                      >
                        Pilih Jenis Perhitungan
                      </label>
                    </b>
                    <div id="calculationMode">
                      <br />
                      <div>
                        <input
                          type="radio"
                          id="calculatePotentialEnergy"
                          name="calculationMode"
                          value="calculatePotentialEnergy"
                          checked={
                            calculationMode === "calculatePotentialEnergy"
                          }
                          onChange={handleModeChange}
                        />
                        <label
                          htmlFor="calculatePotentialEnergy"
                          className="ms-2"
                        >
                          Hitung Energi Potensial
                        </label>
                      </div>
                      <div className="ms-3">
                        <input
                          type="radio"
                          id="calculateMass"
                          name="calculationMode"
                          value="calculateMass"
                          checked={calculationMode === "calculateMass"}
                          onChange={handleModeChange}
                        />
                        <label htmlFor="calculateMass" className="ms-2">
                          Hitung Massa
                        </label>
                      </div>
                      <div className="ms-3">
                        <input
                          type="radio"
                          id="calculateHeight"
                          name="calculationMode"
                          value="calculateHeight"
                          checked={calculationMode === "calculateHeight"}
                          onChange={handleModeChange}
                        />
                        <label htmlFor="calculateHeight" className="ms-2">
                          Hitung Ketinggian
                        </label>
                      </div>
                    </div>
                  </div>
                  <br />
                  {calculationMode !== "calculateMass" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label className="form-label ms-2" htmlFor="massInput">
                          Massa (kilogram)
                        </label>
                      </b>
                      <input
                        id="massInput"
                        type="number"
                        placeholder="80"
                        value={mass}
                        onChange={handleMassChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  {calculationMode !== "calculateHeight" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label
                          className="form-label ms-2"
                          htmlFor="heightInput"
                        >
                          Ketinggian (meter)
                        </label>
                      </b>
                      <input
                        id="heightInput"
                        type="number"
                        placeholder="10"
                        value={height}
                        onChange={handleHeightChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  {calculationMode !== "calculatePotentialEnergy" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label
                          className="form-label ms-2"
                          htmlFor="potentialEnergyInput"
                        >
                          Energi Potensial (joule)
                        </label>
                      </b>
                      <input
                        id="potentialEnergyInput"
                        type="number"
                        placeholder="320"
                        value={potentialEnergy}
                        onChange={handlePotentialEnergyChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  <br />
                  <button
                    className="btn btn-success"
                    style={{ fontSize: "20px" }}
                    onClick={handleCalculateClick}
                  >
                    Hitung
                  </button>
                  <br/>                  <div>
                    { (
                      <>
                        {error !== "" && (
                          <p className="p-3 mb-2 bg-warning text-white">
                            Kesalahan: {error}
                          </p>
                        )}
                        {calculationMode === "calculatePotentialEnergy" &&
                          potentialEnergy !== "" && (
                            <p className="p-3 mb-2 bg-success text-white">
                              Energi Potensial (joule): {potentialEnergy} joule
                            </p>
                          )}
                        {calculationMode === "calculateMass" && mass !== "" && (
                          <p className="p-3 mb-2 bg-success text-white">
                            Massa (kilogram): {mass} kilogram
                          </p>
                        )}
                        {calculationMode === "calculateHeight" &&
                          height !== "" && (
                            <p className="p-3 mb-2 bg-success text-white">
                              Ketinggian (meter): {height} meter
                            </p>
                          )}
                        {showSteps && getSolutionSteps()}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
              <hr />
            </div>
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList></MainCalculatorsList>
          <br />
        </div>
         </div>
</div>
  );
}
export default PotentialEnergyCalculator;